import { useState } from "react";

// ─── Color tokens (matches Reimbursement theme) ───────────────────────────────
const C = {
  bg:              "var(--pay-bg)",
  surface:         "var(--pay-surface)",
  surfaceSecondary:"var(--pay-surface-2)",
  border:          "var(--pay-border)",
  text:            "var(--pay-text)",
  muted:           "var(--pay-text-muted)",
  purple:          "#8660F8",
};

const s = {
  input: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "10px 16px",
    color: C.text,
    fontSize: 15,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    background: C.surfaceSecondary,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "10px 16px",
    color: C.text,
    fontSize: 15,
    outline: "none",
    appearance: "none",
    cursor: "pointer",
    width: "100%",
  },
  btnPrimary: {
    background: C.purple,
    border: "none",
    borderRadius: 14,
    padding: "10px 20px",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 0 20px rgba(134,96,248,0.25)",
  },
  btnOutline: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "10px 20px",
    color: C.text,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};

// ─── Policy data ──────────────────────────────────────────────────────────────
const POLICIES = [
  { name: "Engineer Standard",    appliesTo: "Engineer",        cap: "₹5,000",  categories: "Travel, Food, Fuel", employees: 42,  status: "Active",   updated: "16 Apr" },
  { name: "Manager Premium",      appliesTo: "Manager",         cap: "₹8,000",  categories: "All 4",              employees: 18,  status: "Active",   updated: "14 Apr" },
  { name: "Executive Basic",      appliesTo: "Executive",       cap: "₹3,000",  categories: "Food, Travel",       employees: 56,  status: "Active",   updated: "11 Apr" },
  { name: "Senior Manager Elite", appliesTo: "Senior Manager",  cap: "₹12,000", categories: "All 4",              employees: 12,  status: "Active",   updated: "08 Apr" },
  { name: "Senior Engineer Plus", appliesTo: "Senior Engineer", cap: "₹6,500",  categories: "All 4",              employees: 20,  status: "Active",   updated: "05 Apr" },
  { name: "Inactive Legacy Policy",appliesTo: "Old Team",       cap: "₹2,000",  categories: "Travel",             employees: 12,  status: "Inactive", updated: "02 Mar" },
];

const COVERAGE = [
  { label: "Executive",        count: 56,  pct: 38, gradient: "linear-gradient(90deg,#3b82f6,#60a5fa)" },
  { label: "Engineer",         count: 42,  pct: 28, gradient: `linear-gradient(90deg,${C.purple},#a78bfa)` },
  { label: "Senior Engineer",  count: 20,  pct: 13, gradient: "linear-gradient(90deg,#f59e0b,#fbbf24)" },
  { label: "Manager",          count: 18,  pct: 12, gradient: "linear-gradient(90deg,#22c55e,#4ade80)" },
  { label: "Senior Manager",   count: 12,  pct: 8,  gradient: "linear-gradient(90deg,#ec4899,#f472b6)" },
];

const RECENT_CHANGES = [
  { icon: "fa-pencil",  iconBg: `rgba(134,96,248,0.1)`, iconColor: C.purple,   text: "Sarah updated Engineer Travel Cap to ₹2,500", time: "16 Apr, 10:42 AM" },
  { icon: "fa-clone",   iconBg: "rgba(59,130,246,0.1)", iconColor: "#3b82f6",  text: "Admin cloned Manager Policy",                  time: "14 Apr, 03:15 PM" },
  { icon: "fa-ban",     iconBg: "rgba(239,68,68,0.1)",  iconColor: "#ef4444",  text: "Finance deactivated Legacy Rule",               time: "11 Apr, 11:20 AM" },
  { icon: "fa-plus",    iconBg: "rgba(34,197,94,0.1)",  iconColor: "#22c55e",  text: "HR created Senior Engineer Plus policy",        time: "05 Apr, 09:30 AM" },
];

// ─── PolicyDashboard Component ────────────────────────────────────────────────
const PolicyDashboard = ({ onClose }) => {
  const [search, setSearch]           = useState("");
  const [designation, setDesignation] = useState("");
  const [branch, setBranch]           = useState("");
  const [statusFilter, setStatus]     = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const filtered = POLICIES.filter(p => {
    const matchSearch      = !search      || p.name.toLowerCase().includes(search.toLowerCase());
    const matchDesignation = !designation || p.appliesTo.toLowerCase().includes(designation.toLowerCase());
    const matchStatus      = !statusFilter || p.status === statusFilter;
    const matchInactive    = showInactive  || p.status !== "Inactive";
    return matchSearch && matchDesignation && matchStatus && matchInactive;
  });

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #050505; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
        .pd-row:hover { background: ${C.surfaceSecondary} !important; transition: background 0.15s; }
        .pd-btn-outline:hover { border-color: ${C.purple} !important; }
      `}</style>

      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Outfit', sans-serif", color: C.text }}>

        {/* ── PAGE HEADER ── */}
        <section style={{ padding: "28px 32px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            {/* Back button */}
            <button onClick={onClose} style={{ ...s.btnOutline, padding: "10px 14px", marginTop: 2 }} title="Back to Reimbursement">
              <i className="fa-solid fa-arrow-left" style={{ fontSize: 12 }} />
            </button>
            <div>
              <h1 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: "0 0 6px" }}>Policy Limits Dashboard</h1>
              <p style={{ fontSize: 15, color: C.muted, margin: 0 }}>Manage reimbursement eligibility rules by designation, branch and employee group.</p>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <button className="pd-btn-outline" style={{ ...s.btnOutline }}>
              <i className="fa-solid fa-file-import" style={{ fontSize: 13 }} /> Import Policies
            </button>
            <button className="pd-btn-outline" style={{ ...s.btnOutline }}>
              <i className="fa-solid fa-download" style={{ fontSize: 13 }} /> Export Report
            </button>
            <button style={{ ...s.btnPrimary }}>
              <i className="fa-solid fa-plus" style={{ fontSize: 13 }} /> Add Policy
            </button>
          </div>
        </section>

        <div style={{ padding: "24px 32px" }}>

          {/* ── KPI CARDS ── */}
          <section style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 }}>
            {[
              { icon: "fa-file-lines",   iconBg: `rgba(134,96,248,0.1)`, iconColor: C.purple,   label: "Total Policies",    value: "12",  sub: "All created policies"    },
              { icon: "fa-circle-check", iconBg: "rgba(34,197,94,0.1)",  iconColor: "#22c55e",  label: "Active Policies",   value: "9",   sub: "Currently in use"         },
              { icon: "fa-users",        iconBg: "rgba(59,130,246,0.1)", iconColor: "#3b82f6",  label: "Employees Covered", value: "148", sub: "Under active policies"    },
              { icon: "fa-clock",        iconBg: "rgba(245,158,11,0.1)", iconColor: "#f59e0b",  label: "Pending Review",    value: "3",   sub: "Rules need attention"     },
            ].map((k, i) => (
              <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: k.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className={`fa-solid ${k.icon}`} style={{ color: k.iconColor, fontSize: 20 }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 13, color: C.muted, margin: "0 0 4px" }}>{k.label}</p>
                    <p style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: 0 }}>{k.value}</p>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{k.sub}</p>
              </div>
            ))}
          </section>

          {/* ── FILTER BAR ── */}
          <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 20px", marginBottom: 28 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 14, alignItems: "center" }}>
              <div style={{ position: "relative" }}>
                <i className="fa-solid fa-search" style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 12 }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search policy name..."
                  style={{ ...s.input, paddingLeft: 44 }} />
              </div>
              <select value={designation} onChange={e => setDesignation(e.target.value)} style={s.select}>
                <option value="">All Designations</option>
                <option value="Engineer">Engineer</option>
                <option value="Manager">Manager</option>
                <option value="Executive">Executive</option>
                <option value="Senior">Senior</option>
              </select>
              <select value={branch} onChange={e => setBranch(e.target.value)} style={s.select}>
                <option value="">All Branches</option>
                <option>Bangalore</option>
                <option>Mumbai</option>
                <option>Delhi</option>
              </select>
              <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={s.select}>
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 16px", cursor: "pointer" }}
                onClick={() => setShowInactive(p => !p)}>
                {/* Toggle */}
                <div style={{ width: 44, height: 24, borderRadius: 12, background: showInactive ? C.purple : C.surfaceSecondary, border: `1px solid ${showInactive ? C.purple : C.border}`, position: "relative", flexShrink: 0 }}>
                  <div style={{ position: "absolute", top: 3, left: showInactive ? 22 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                </div>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 500, whiteSpace: "nowrap" }}>Show Inactive</span>
              </div>
            </div>
          </section>

          {/* ── MAIN LAYOUT: TABLE + SIDEBAR ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 24 }}>

            {/* LEFT — Policy Table */}
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <i className="fa-solid fa-list" style={{ color: C.purple }} />
                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.text, margin: 0 }}>All Reimbursement Policies</h3>
              </div>
              <div style={{ overflowX: "auto" }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1.2fr 0.9fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr", padding: "12px 24px", background: "rgba(22,16,50,0.5)", borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <div>Policy Name</div><div>Applies To</div><div>Monthly Cap</div>
                  <div>Categories</div><div>Employees</div><div>Status</div>
                  <div>Last Updated</div><div>Action</div>
                </div>
                {/* Rows */}
                {filtered.map((p, i) => (
                  <div key={i} className="pd-row" style={{ display: "grid", gridTemplateColumns: "1.8fr 1.2fr 0.9fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr", padding: "14px 24px", borderBottom: `1px solid ${C.border}`, alignItems: "center", opacity: p.status === "Inactive" ? 0.6 : 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.appliesTo}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{p.cap}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.categories}</div>
                    <div>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: `rgba(134,96,248,0.1)`, border: `1px solid rgba(134,96,248,0.3)`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: C.purple }}>
                        <i className="fa-solid fa-users" style={{ fontSize: 11 }} /> {p.employees}
                      </span>
                    </div>
                    <div>
                      <span style={{ display: "inline-flex", padding: "4px 12px", background: p.status === "Active" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${p.status === "Active" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: p.status === "Active" ? "#22c55e" : "#ef4444" }}>
                        {p.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>{p.updated}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button style={{ padding: "5px 12px", background: p.status === "Active" ? `rgba(134,96,248,0.1)` : C.surfaceSecondary, border: `1px solid ${p.status === "Active" ? "rgba(134,96,248,0.3)" : C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: p.status === "Active" ? C.purple : C.muted, cursor: "pointer" }}>View</button>
                      <button style={{ width: 32, height: 32, background: C.surfaceSecondary, border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="fa-solid fa-ellipsis-vertical" style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  </div>
                ))}
                {/* Pagination */}
                <div style={{ padding: "20px 24px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>Showing {filtered.length} of {POLICIES.length} policies</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {["chevron-left", "1", "2", "chevron-right"].map((b, i) => (
                      <button key={i} style={{ width: 36, height: 36, background: b === "1" ? C.purple : C.surfaceSecondary, border: `1px solid ${b === "1" ? C.purple : C.border}`, borderRadius: 8, color: b === "1" ? "#fff" : C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: b === "1" ? 700 : 400 }}>
                        {b.startsWith("chevron") ? <i className={`fa-solid fa-${b}`} style={{ fontSize: 11 }} /> : b}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — Sidebar */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Coverage Breakdown */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-chart-pie" style={{ color: C.purple }} /> Coverage Breakdown
                </h3>
                {COVERAGE.map((c, i) => (
                  <div key={i} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>{c.label}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{c.count}</span>
                    </div>
                    <div style={{ width: "100%", background: C.surfaceSecondary, height: 8, borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${c.pct}%`, height: "100%", borderRadius: 4, background: c.gradient }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Policy Alerts */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: C.purple }} /> Policy Alerts
                </h3>
                {[
                  { icon: "fa-layer-group",  color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)", title: "Overlapping Policies",  sub: "2 policies have conflicting rules" },
                  { icon: "fa-user-slash",   color: "#ef4444", bg: "rgba(239,68,68,0.05)",  border: "rgba(239,68,68,0.2)",  title: "Unassigned Employees",  sub: "3 employees without policy" },
                  { icon: "fa-link-slash",   color: "#f59e0b", bg: "rgba(245,158,11,0.05)", border: "rgba(245,158,11,0.2)", title: "Inactive Mapping",       sub: "1 inactive rule still mapped" },
                ].map((a, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 12, background: a.bg, border: `1px solid ${a.border}`, borderRadius: 10, marginBottom: 10 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ color: a.color, fontSize: 12, marginTop: 2 }} />
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: "0 0 4px" }}>{a.title}</p>
                      <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{a.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20 }}>Quick Actions</h3>
                {[
                  { icon: "fa-plus",       label: "Add Policy",             primary: true  },
                  { icon: "fa-users-gear", label: "Bulk Assign",            primary: false },
                  { icon: "fa-ban",        label: "Deactivate Old Rules",   primary: false },
                  { icon: "fa-download",   label: "Download Matrix",        primary: false },
                ].map((a, i) => (
                  <button key={i} className={a.primary ? "" : "pd-btn-outline"} style={{ ...(a.primary ? s.btnPrimary : s.btnOutline), width: "100%", justifyContent: "center", marginBottom: 10 }}>
                    <i className={`fa-solid ${a.icon}`} style={{ fontSize: 13 }} /> {a.label}
                  </button>
                ))}
              </div>

              {/* Recent Changes */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-clock-rotate-left" style={{ color: C.purple }} /> Recent Changes
                </h3>
                {RECENT_CHANGES.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: r.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <i className={`fa-solid ${r.icon}`} style={{ color: r.iconColor, fontSize: 12 }} />
                    </div>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 500, color: C.text, margin: "0 0 4px" }}>{r.text}</p>
                      <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{r.time}</p>
                    </div>
                  </div>
                ))}
                <button style={{ width: "100%", background: "none", border: "none", fontSize: 13, color: C.purple, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                  View All Changes →
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PolicyDashboard;